import Phaser from "phaser";
import { getResourcePileLabel } from "@/core";
import type { ResourceType } from "@/core";
import type { GameApp } from "../app/GameApp";
import { ACTIVE_DECK_SIZE, getCard, type CardDef, type Stats } from "../core/roscha";
import { renderGameCard } from "./TavernOverlay";
import { mountDom, mountOverlay, type DomOverlay } from "@/ui/domHelpers";

type DeckTab = "class" | "common";

const RESOURCE_ORDER: ResourceType[] = ["gold", "mana"];

const RESOURCE_ICON_SRC: Record<ResourceType, string> = {
  gold: "/map/icons/icon_gold.png",
  mana: "/map/icons/icon_mana.png",
};

export class HudScene extends Phaser.Scene {
  private app!: GameApp;
  private dom?: Phaser.GameObjects.DOMElement;
  private root?: HTMLElement;
  private unsubWorld?: () => void;
  private unsubEnergy?: () => void;
  private deckOverlay?: DomOverlay;
  private deckWorking: string[] = [];
  private deckTab: DeckTab = "class";
  private lastHeroClickTime = 0;

  constructor() {
    super({ key: "HudScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.setZoom(1);

    this.mount();

    const s = this.app.getWorldState();
    if (s) {
      this.updateResources(s.hero.inventory.resources ?? {});
      this.updateLevel();
    }
    this.updateEnergy(this.app.getEnergy());

    this.unsubWorld = this.app.onWorldState((state) => {
      this.updateResources(state.hero.inventory.resources ?? {});
      this.updateLevel();
    });

    this.unsubEnergy = this.app.onEnergyUpdate((energy) => {
      this.updateEnergy(energy);
    });

  }

  shutdown() {
    this.unsubWorld?.();
    this.unsubEnergy?.();
    this.dom = undefined;
    this.root = undefined;
  }

  private mount() {
    const resourcesHtml = RESOURCE_ORDER
      .map((type) => `
        <div class="hud__resource" data-resource="${type}" title="${getResourcePileLabel(type)}">
          <img class="hud__resource-icon" src="${RESOURCE_ICON_SRC[type]}" alt="${type}">
          <span class="hud__resource-value" data-value="${type}">0</span>
        </div>
      `)
      .join("");

    const html = `
      <div class="hud">
        <div class="hud__top">
          <div class="hud__resources">${resourcesHtml}</div>
          <div class="hud__meta">
            <div class="hud__chip hud__chip--energy" title="Энергия — действия на ходу">
              <span class="hud__chip-label">Энергия</span>
              <span class="hud__chip-value" data-energy>0</span>
            </div>
            <div class="hud__chip hud__chip--hp" title="Здоровье">
              <span class="hud__chip-label">HP</span>
              <span class="hud__chip-value" data-hp>0</span>
              <span class="hud__chip-aux" data-maxhp>/ 0</span>
            </div>
            <div class="hud__chip hud__chip--level" title="Уровень героя и опыт">
              <span class="hud__chip-label">Уровень</span>
              <span class="hud__chip-value" data-level>1</span>
              <span class="hud__chip-aux" data-xp>0 / 0 XP</span>
            </div>
          </div>
        </div>

        <nav class="hud__nav hud__nav--triangle">
          <button class="hud__nav-btn hud__nav-btn--top" data-action="hero" title="Герой / колода">
            <span class="hud__nav-icon">⚔</span>
            <span class="hud__nav-label">Герой</span>
          </button>
          <button class="hud__nav-btn hud__nav-btn--left" data-action="town" title="Город">
            <span class="hud__nav-icon">🏰</span>
            <span class="hud__nav-label">Город</span>
          </button>
          <button class="hud__nav-btn hud__nav-btn--right" data-action="equipment" title="Экипировка">
            <span class="hud__nav-icon">🎒</span>
            <span class="hud__nav-label">Снаряжение</span>
          </button>
        </nav>
      </div>
    `;

    this.dom = mountDom(this, html, { clickThrough: true });
    this.root = this.dom.node as HTMLElement;

    this.root.querySelector('[data-action="hero"]')?.addEventListener("click", () => {
      if (this.app.isMovementLocked()) return;
      this.onHeroIconClick();
    });
    this.root.querySelector('[data-action="town"]')?.addEventListener("click", () => {
      if (this.app.isMovementLocked()) return;
      this.app.openTown();
    });
    this.root.querySelector('[data-action="equipment"]')?.addEventListener("click", () => {
      if (this.app.isMovementLocked()) return;
      this.app.openEquipment();
    });
  }

  private updateResources(resources: Partial<Record<ResourceType, number>>) {
    if (!this.root) return;
    RESOURCE_ORDER.forEach((type) => {
      const qty = resources[type] ?? 0;
      const el = this.root!.querySelector(`[data-value="${type}"]`) as HTMLElement | null;
      const wrap = this.root!.querySelector(`[data-resource="${type}"]`) as HTMLElement | null;
      if (el) el.textContent = String(qty);
      if (wrap) wrap.dataset.empty = qty <= 0 ? "true" : "false";
    });
  }

  private updateEnergy(energy: number) {
    const el = this.root?.querySelector("[data-energy]") as HTMLElement | null;
    if (el) el.textContent = String(energy);
  }

  private updateLevel() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.root) return;
    const next = 25 * hero.level * hero.level + 25;
    const lvlEl = this.root.querySelector("[data-level]") as HTMLElement | null;
    const xpEl = this.root.querySelector("[data-xp]") as HTMLElement | null;
    const hpEl = this.root.querySelector("[data-hp]") as HTMLElement | null;
    const maxHpEl = this.root.querySelector("[data-maxhp]") as HTMLElement | null;
    if (lvlEl) lvlEl.textContent = String(hero.level);
    if (xpEl) xpEl.textContent = `${hero.xp} / ${next} XP`;
    if (hpEl) hpEl.textContent = String(hero.hp);
    if (maxHpEl) maxHpEl.textContent = `/ ${hero.maxHp}`;
  }

  private onHeroIconClick() {
    const now = Date.now();
    if (now - this.lastHeroClickTime < 400) {
      this.showDeckModal();
    } else {
      this.centerOnHero();
    }
    this.lastHeroClickTime = now;
  }

  private centerOnHero() {
    this.game.events.emit("hud:centerHero");
  }

  private showDeckModal() {
    if (this.deckOverlay) { this.closeDeckModal(); return; }
    const ws = this.app.getWorldState();
    if (!ws) return;

    this.deckWorking = [...ws.hero.deck];
    this.deckTab = "class";

    this.deckOverlay = mountOverlay(this, {
      title: "Колода",
      subtitle: "Собери боевую колоду из коллекции",
      bodyHtml: `<div class="deck-mgr" data-deck-root></div>`,
      onClose: () => this.closeDeckModal(),
      inBody: true,
    });

    const root = this.deckOverlay.body.querySelector("[data-deck-root]") as HTMLElement | null;
    root?.addEventListener("click", (e) => this.onDeckClick(e));
    this.renderDeckManager();
  }

  private heroStats(): Stats {
    return this.app.getWorldState()?.hero.stats ?? { strength: 0, dexterity: 0, intelligence: 0, wisdom: 0, constitution: 0 };
  }

  private resolveCard(id: string): CardDef | null {
    try { return getCard(id); } catch { return null; }
  }

  private cardPool(card: CardDef): DeckTab {
    if (card.classId == null) return "common";
    return "class";
  }

  private renderDeckManager() {
    const ws = this.app.getWorldState();
    const root = this.deckOverlay?.body.querySelector("[data-deck-root]") as HTMLElement | null;
    if (!ws || !root) return;
    const stats = this.heroStats();

    const available = [...ws.hero.collection];
    for (const id of this.deckWorking) {
      const i = available.indexOf(id);
      if (i >= 0) available.splice(i, 1);
    }

    const groupCounts = (ids: string[]): Map<string, number> => {
      const m = new Map<string, number>();
      for (const id of ids) m.set(id, (m.get(id) ?? 0) + 1);
      return m;
    };

    const renderCells = (counts: Map<string, number>, action: "add" | "remove", disabled: boolean): string =>
      [...counts.entries()].map(([id, n]) => {
        const c = this.resolveCard(id);
        if (!c) return "";
        return `<div class="deck-pick${disabled ? " is-disabled" : ""}" data-card-id="${id}" data-action="${action}">${renderGameCard(c, stats, undefined, n, ws.hero.domainId ?? undefined)}</div>`;
      }).join("");

    const full = this.deckWorking.length >= ACTIVE_DECK_SIZE;
    const deckCells = renderCells(groupCounts(this.deckWorking), "remove", false);
    const availIds = available.filter((id) => {
      const c = this.resolveCard(id);
      return c ? this.cardPool(c) === this.deckTab : false;
    });
    const availCells = renderCells(groupCounts(availIds), "add", full);

    const tab = (key: DeckTab, label: string): string =>
      `<button class="deck-tab${this.deckTab === key ? " is-active" : ""}" data-tab="${key}">${label}</button>`;

    const counterClass = this.deckWorking.length === ACTIVE_DECK_SIZE ? "is-full" : "is-warn";

    root.innerHTML = `
      <div class="deck-mgr__counter ${counterClass}">Колода ${this.deckWorking.length} / ${ACTIVE_DECK_SIZE}</div>
      <p class="deck-mgr__hint">Клик по карте перекладывает одну копию. У стопки ×N кликни нужное число раз.</p>
      <div class="deck-mgr__cols">
        <div class="deck-mgr__col">
          <div class="deck-mgr__col-title">Активная колода</div>
          <div class="deck-mgr__grid">${deckCells || `<p class="deck-mgr__empty">Колода пуста</p>`}</div>
        </div>
        <div class="deck-mgr__col">
          <div class="deck-mgr__tabs">${tab("class", "Класс")}${tab("common", "Общие")}</div>
          <div class="deck-mgr__grid">${availCells || `<p class="deck-mgr__empty">Нет карт</p>`}</div>
        </div>
      </div>
    `;
  }

  private onDeckClick(e: Event) {
    const target = e.target as HTMLElement;
    const tabBtn = target.closest("[data-tab]") as HTMLElement | null;
    if (tabBtn) {
      this.deckTab = tabBtn.dataset.tab as DeckTab;
      this.renderDeckManager();
      return;
    }
    const pick = target.closest("[data-card-id]") as HTMLElement | null;
    if (!pick) return;
    const id = pick.dataset.cardId as string;
    if (pick.dataset.action === "remove") {
      const i = this.deckWorking.indexOf(id);
      if (i >= 0) this.deckWorking.splice(i, 1);
    } else {
      if (this.deckWorking.length >= ACTIVE_DECK_SIZE) return;
      this.deckWorking.push(id);
    }
    this.app.setActiveDeck(this.deckWorking);
    this.renderDeckManager();
  }

  private closeDeckModal() {
    if (!this.deckOverlay) return;
    this.app.setActiveDeck(this.deckWorking);
    this.deckOverlay.destroy();
    this.deckOverlay = undefined;
  }
}
